import * as React from "react";

import { NavigationToggle } from "@/shared/components/NavigationToggle";

import { RobotoTopBarActions } from "../RobotoNavigation/RobotoTopBar";

import styles from "./PageLayout.module.css";

export interface PageLayoutProps extends React.PropsWithChildren {
  title?: string;
  headerContentLeft?: React.ReactNode | string;
  headerContentRight?: React.ReactNode | string;
  /**
   * Action bar that stays flush at the page bottom while the content
   * scrolls. Use for full-page form actions (e.g. a Cancel / submit bar).
   *
   * The bar (border, padding, background) renders only when this prop holds
   * content. Caveat: a fragment counts as content even when
   * everything inside it is empty — `footer={<>{cond && <Actions />}</>}`
   * with `cond` false paints a visible empty bar. Hoist the condition to the
   * prop instead: `footer={cond ? <Actions /> : undefined}`.
   */
  footer?: React.ReactNode;
}

export function PageLayout(props: PageLayoutProps) {
  React.useEffect(
    function setPageTitle() {
      if (props.title) {
        document.title = props.title;
      } else {
        document.title = "Roboto";
      }
    },
    [props.title],
  );

  return (
    <main className={styles.page}>
      <header className={styles.pageHeader}>
        <div className={styles.pageHeaderLeft}>
          <NavigationToggle />
          <h1 className={styles.pageHeaderTitle}>{props.headerContentLeft}</h1>
        </div>
        <div className={styles.pageHeaderRight}>
          {props.headerContentRight}
          <div className={styles.rightActionsContainer}>
            {props.headerContentRight && <div className={styles.spacer} />}
            <RobotoTopBarActions />
          </div>
        </div>
      </header>
      <div className={styles.pageContent}>{props.children}</div>
      <PageFooter>{props.footer}</PageFooter>
    </main>
  );
}

function PageFooter(props: React.PropsWithChildren) {
  // `React.Children.toArray` drops what React won't render (null, undefined,
  // booleans) and flattens arrays, so `footer={[]}` doesn't paint an empty
  // bar; the truthiness filter drops empty strings. Fragments are opaque to
  // it; that hazard is documented on the `footer` prop.
  const renderable = React.Children.toArray(props.children).filter(Boolean);
  if (renderable.length === 0) {
    return null;
  }
  return <footer className={styles.pageFooter}>{props.children}</footer>;
}
